/* Name lamp builder (<lamp-builder>, sections/squigli-lamp-builder.liquid).
   The name becomes a stack of blocks (A-Z and the 12 website symbols, which travel through the input as emoji like on
   the name studio). Turning the lamp on darkens the whole page except the lamp (html.squigli-lights-out) and lights
   the "wall" behind it with the Squigli logo. When the page opens the lamp switches itself on and off once; after that
   (or as soon as the visitor touches the switch) it's the visitor's. Reduced motion: no automatic switching.
   Designs can be shared as links, like the name studio's: ?name=LUNA(heart)&colors=pink-blue&base=flower-white&shade=pleated
   (symbols in brackets, colours by their palette names; the base is its shape and colour). */
(() => {
  if (customElements.get('lamp-builder')) return;
  const VS = /️/g;   // emoji variation selector: '❤️' and '❤' are the same symbol
  // Example names shown before anything is typed, like the name studio's: they change every few seconds with a spin,
  // each with gender-fitting block colours, base colour, and its own base and shade. Hexes are the section's colours.
  const EX_GIRLS = ['LAYLA', 'MAYA', 'NOUR', 'YARA', 'SARA', 'RIMA', 'LEA', 'JANA', 'FARAH', 'DANA', 'HANA', 'MIRA', 'TALA', 'LINA', 'MIA', 'NAYA'];
  const EX_BOYS = ['KARIM', 'OMAR', 'ALI', 'RAMI', 'ZIAD', 'SAMI', 'JAD', 'MARWAN', 'TAREK', 'HADI', 'ELIE', 'RAYAN', 'ADAM', 'SAM', 'ZEIN', 'NABIL'];
  const EX_PAL = {
    girl: [['#F0727F', '#8983D2', '#F5C61B'], ['#8983D2', '#F0727F'], ['#F0727F', '#F5C61B', '#8983D2', '#E9793C'], ['#F0727F', '#8983D2', '#F0727F', '#F5C61B']],
    boy: [['#0B44B0', '#02A751', '#F5C61B'], ['#0B44B0', '#E9793C'], ['#02A751', '#0B44B0', '#F5C61B', '#E9793C'], ['#0B44B0', '#F5C61B', '#02A751']],
  };
  const EX_BASE = {girl: ['#F4F1EA', '#F0727F', '#8983D2'], boy: ['#F4F1EA', '#0B44B0', '#02A751', '#F5C61B']};
  const EX_EVERY = 3600;
  const LINK_KEYS = ['name', 'colors', 'base', 'shade'];

  class LampBuilder extends HTMLElement {
    connectedCallback() {
      this.i18n = JSON.parse(this.querySelector('[data-i18n]').textContent);
      this.symbols = {};
      for (const [emoji, s] of Object.entries(JSON.parse(this.dataset.symbols || '{}'))) this.symbols[emoji.replace(VS, '')] = s;
      this.max = +this.dataset.max || 8;
      this.min = +this.dataset.min || 1;
      this.input = this.querySelector('[data-input]');
      this.stage = this.querySelector('[data-stage]');
      this.colorNames = {};
      this.querySelectorAll('[data-color-name]').forEach((b) => (this.colorNames[(b.dataset.cubeColor || b.dataset.baseColor).toUpperCase()] = b.dataset.colorName));
      const firstPreset = this.querySelector('[data-preset]');
      this.palette = firstPreset.dataset.colors.split(',');
      this.rainbow = this.palette;     // the default pattern: links leave it out
      // block colours, like the name studio: one colour per block. fill = how new blocks get theirs:
      // 'pattern' (a quick style), 'solid' (one colour for all) or 'custom' (painted block by block)
      this.fill = {mode: 'pattern', list: this.palette};
      this.sel = null;                 // the block picked for its own colour (index into tokens), or null
      this.chipHexes = [...this.querySelectorAll('[data-cube-color]')].map((b) => b.dataset.cubeColor);
      this.colorLabels = {};
      this.querySelectorAll('[data-cube-color]').forEach((b) => (this.colorLabels[b.dataset.cubeColor.toUpperCase()] = b.getAttribute('aria-label')));
      this.symInfo = {};               // symbol slug -> {label, url} for the block tiles
      this.querySelectorAll('[data-insert]').forEach((b) => {
        const s = this.symbols[b.dataset.insert.replace(VS, '')], icon = b.querySelector('.lamp__sym-icon');
        if (s) this.symInfo[s.slug] = {label: b.title, url: icon ? icon.style.getPropertyValue('--symbol-url').trim() : 'none'};
      });
      this.base = 'circle';
      this.baseColor = (this.querySelector('[data-base-color][aria-pressed="true"]') || this.querySelector('[data-base-color]')).dataset.baseColor;
      this.shade = 'round';
      this.tokens = [];
      this.lit = false;
      this.manual = false;
      this.busyUntil = 0;
      this.still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.cycling = !this.still;      // the example names keep changing until the visitor touches anything
      this.example = null;
      this.cycleHold = 0;
      this.colors = this.shownTokens().map((_, i) => this.colorAt(i));
      this.input.addEventListener('input', () => this.readInput());
      this.querySelectorAll('[data-insert]').forEach((b) => b.addEventListener('click', () => this.insert(b.dataset.insert)));
      this.querySelectorAll('[data-preset]').forEach((b) => b.addEventListener('click', () => {
        this.palette = b.dataset.colors.split(',');
        this.fill = {mode: 'pattern', list: this.palette};
        this.colors = this.colors.map((_, i) => this.colorAt(i));
        this.sel = null;
        this.pressed('[data-preset]', b); this.pressed('[data-cube-color]', null); this.update();
      }));
      this.querySelectorAll('[data-cube-color]').forEach((b) => b.addEventListener('click', () => this.applyColor(b.dataset.cubeColor, b)));
      const surprise = this.querySelector('[data-surprise]');
      if (surprise) surprise.addEventListener('click', () => {
        this.fill = {mode: 'custom'};
        this.colors = this.colors.map(() => this.chipHexes[Math.floor(Math.random() * this.chipHexes.length)]);
        this.sel = null;
        this.pressed('[data-preset]', null); this.pressed('[data-cube-color]', null); this.update();
      });
      this.querySelectorAll('[data-base-color]').forEach((b) => b.addEventListener('click', () => {
        this.baseColor = b.dataset.baseColor; this.pressed('[data-base-color]', b); this.lamp && this.lamp.setBase(this.base, this.baseColor); this.update();
      }));
      this.querySelectorAll('[data-base]').forEach((b) => b.addEventListener('click', () => {
        this.base = b.dataset.base; this.checked('[data-base]', b); this.lamp && this.lamp.setBase(this.base, this.baseColor); this.update();
      }));
      this.querySelectorAll('[data-shade]').forEach((b) => b.addEventListener('click', () => {
        this.shade = b.dataset.shade; this.checked('[data-shade]', b); this.lamp && this.lamp.setShade(this.shade); this.update();
      }));
      // arrow keys inside the option groups, like radio buttons
      this.querySelectorAll('[role="radiogroup"]').forEach((g) => g.addEventListener('keydown', (e) => {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
        const opts = [...g.querySelectorAll('[role="radio"]')], i = opts.indexOf(document.activeElement);
        if (i < 0) return;
        const next = opts[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : opts.length - 1)) % opts.length];
        next.focus(); next.click(); e.preventDefault();
      }));

      // the switch, and the dark page around the lit lamp (a tap on the dark brings the room lights back)
      this.switchBtn = this.querySelector('[data-switch]');
      const flip = (on) => { this.manual = true; this.clack(on); this.setLit(on); };
      this.switchBtn.addEventListener('click', () => flip(!this.lit));
      this.querySelector('[data-night]').addEventListener('click', () => flip(false));
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (this.tour && !this.tour.hidden) this.endTour(); else if (this.lit) flip(false);
      });
      // busy with the controls: room lights on, and the automatic show waits
      const panel = this.querySelector('[data-panel]');
      ['pointerdown', 'focusin', 'keydown'].forEach((ev) => panel.addEventListener(ev, () => this.busy()));
      // the visitor starts: the example they're looking at becomes their design (runs before the control's click)
      ['pointerdown', 'keydown', 'input'].forEach((ev) => panel.addEventListener(ev, () => this.stopCycle(), true));
      this.stage.addEventListener('pointerdown', () => { this.cycleHold = performance.now() + 6000; });   // turning the lamp: wait

      const form = this.querySelector('[data-cart-form]');
      if (form) {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        form.addEventListener('submit', (e) => this.addToCart(e, form));
      }

      // links mirror the design into the address bar, but only on the product's own page
      this.ownsUrl = !!this.dataset.productUrl && location.pathname.replace(/\/$/, '').endsWith(this.dataset.productUrl);
      this.initShare();
      this.readInput();
      this.readLink();
      this.initTour();
      this.boot3D();
      this.watchView();
    }

    busy() { this.busyUntil = performance.now() + 9000; if (this.lit && !this.manual) this.setLit(false); }

    t(key, vars = {}) {
      // Shopify's t filter HTML-escapes ("I'd" arrives as "I&#39;d"); decode, these strings end up as plain text
      const ta = document.createElement('textarea');
      ta.innerHTML = this.i18n[key] || key;
      let s = ta.value;
      for (const [k, v] of Object.entries(vars)) s = s.split(`%${k}%`).join(v);
      return s;
    }

    pressed(sel, on) { this.querySelectorAll(sel).forEach((b) => b.setAttribute('aria-pressed', String(b === on))); }
    checked(sel, on) { this.querySelectorAll(sel).forEach((b) => b.setAttribute('aria-checked', String(b === on))); }

    // ---- name ----
    insert(emoji) {
      if (this.tokens.length >= this.max) { this.flashHint(this.t('hint_max', {max: this.max})); return; }
      const el = this.input, at = el.selectionStart ?? el.value.length, end = el.selectionEnd ?? at;
      el.value = el.value.slice(0, at) + emoji + el.value.slice(end);
      const caret = at + emoji.length;
      el.focus({preventScroll: true});
      el.setSelectionRange(caret, caret);
      this.readInput();
    }

    readInput() {
      const raw = this.input.value.replace(VS, '');
      let clean = '', latin = false, tokens = [];
      for (const ch of raw) {
        const up = ch.toUpperCase();
        if (/[A-Z]/.test(up)) { if (tokens.length < this.max) { tokens.push(up); clean += up; } }
        else if (this.symbols[ch]) { if (tokens.length < this.max) { tokens.push(this.symbols[ch].slug); clean += ch; } }
        else if (/\p{L}/u.test(ch)) latin = true;
      }
      if (clean !== raw) {
        const pos = this.input.selectionStart;
        this.input.value = clean;
        try { this.input.setSelectionRange(Math.min(pos, clean.length), Math.min(pos, clean.length)); } catch (e) { /* not focused */ }
      }
      const before = this.shownTokens();
      this.tokens = tokens;
      this.latin = latin;
      const after = this.shownTokens();
      if (before.join('|') !== after.join('|')) { this.syncColors(before, after); this.sel = null; }
      this.update(true);
    }

    shownTokens() {
      if (this.tokens.length) return this.tokens;
      if (this.example) return this.example.tokens;
      return [...(this.dataset.defaultName || 'LUNA')].filter((c) => /[A-Z]/.test(c));
    }

    // ---- the changing example names ----
    startCycle() {
      if (!this.cycling || this.cycleTimer) return;
      if ('IntersectionObserver' in window) new IntersectionObserver((es) => { this.stageSeen = es[0].isIntersecting; }).observe(this.stage);
      else this.stageSeen = true;
      this.cycleTimer = setInterval(() => this.cycleOnce(), EX_EVERY);
    }
    nextExample() {
      const prev = this.example, girl = prev ? !prev.girl : Math.random() < 0.5, g = girl ? 'girl' : 'boy';
      const pick = (a) => a[Math.floor(Math.random() * a.length)];
      const names = (girl ? EX_GIRLS : EX_BOYS).filter((n) => n.length >= this.min && n.length <= this.max);
      let name = pick(names);
      if (prev && name === prev.name) name = names[(names.indexOf(name) + 1) % names.length];
      const okHex = (h) => this.chipHexes.some((c) => c.toUpperCase() === h.toUpperCase());
      const palette = pick(EX_PAL[g]).filter(okHex);
      const baseHexes = [...this.querySelectorAll('[data-base-color]')].map((b) => b.dataset.baseColor.toUpperCase());
      const baseColor = pick(EX_BASE[g].filter((h) => baseHexes.includes(h.toUpperCase())).concat(baseHexes.length ? [] : [this.baseColor]));
      const others = (list, cur) => list.filter((x) => x !== cur);
      const tokens = [...name];
      return {girl, name, tokens, palette: palette.length ? palette : this.palette, colors: tokens.map((_, i) => (palette.length ? palette : this.palette)[i % (palette.length || this.palette.length)]),
        base: pick(others(['circle', 'square', 'flower'], prev ? prev.base : this.base)), baseColor,
        shade: pick(others(['round', 'rings', 'sphere', 'pleated', 'square'], prev ? prev.shade : this.shade))};
    }
    showExample(ex) {
      this.example = ex;
      this.colors = ex.colors.slice();
      this.sel = null;
      if (this.lamp) { this.lamp.setBase(ex.base, ex.baseColor); this.lamp.setShade(ex.shade); }
      this.update(true);
    }
    cycleOnce() {
      if (!this.cycling || this.tokens.length || document.hidden || this.stageSeen === false || this.lit || performance.now() < this.cycleHold) return;
      const ex = this.nextExample();
      if (this.lamp && this.lamp.spin) {
        this.lamp.spin({mid: () => { if (this.cycling && !this.tokens.length) this.showExample(ex); }, done: () => { if (this.cycling) this.burst(); }});
      } else {                                       // the flat preview: a quick pop instead of the spin
        this.showExample(ex);
        if (this.flatEl) { this.flatEl.classList.remove('is-popping'); void this.flatEl.offsetWidth; this.flatEl.classList.add('is-popping'); }
      }
    }
    stopCycle() {
      if (!this.cycling) return;
      this.cycling = false;
      clearInterval(this.cycleTimer);
      const ex = this.example;
      if (!ex) return;
      // keep what they were looking at: its base, base colour, shade and colour pattern become theirs
      this.base = ex.base; this.shade = ex.shade; this.baseColor = ex.baseColor;
      this.checked('[data-base]', this.querySelector(`[data-base="${ex.base}"]`));
      this.checked('[data-shade]', this.querySelector(`[data-shade="${ex.shade}"]`));
      this.pressed('[data-base-color]', [...this.querySelectorAll('[data-base-color]')].find((b) => b.dataset.baseColor.toUpperCase() === ex.baseColor.toUpperCase()) || null);
      this.palette = ex.palette; this.fill = {mode: 'pattern', list: ex.palette};
      this.pressed('[data-preset]', null);
      if (this.lamp) { this.lamp.setBase(this.base, this.baseColor); this.lamp.setShade(this.shade); }
    }
    burst() {                                        // brand sparkles round the lamp as it lands, like the home page
      const box = document.createElement('span');
      box.className = 'lamp__burst';
      const cols = ['#89caac', '#ffc934', '#af99c8', '#9acedc', '#f39ab6'], w = this.stage.clientWidth;
      for (let i = 0; i < 9; i++) {
        const b = document.createElement('i'), a = (i / 9) * Math.PI * 2 + Math.random() * 0.5, d = w * (0.16 + Math.random() * 0.12);
        b.className = i % 3 === 0 ? 'lamp__spark' : 'lamp__dot';
        b.style.setProperty('--dx', `${(Math.cos(a) * d).toFixed(1)}px`); b.style.setProperty('--dy', `${(Math.sin(a) * d * 0.85).toFixed(1)}px`);
        b.style.setProperty('--c', cols[i % cols.length]); b.style.setProperty('--r', `${(Math.random() * 120 - 60).toFixed(0)}deg`);
        b.style.animationDelay = `${(Math.random() * 0.12).toFixed(2)}s`;
        box.appendChild(b);
      }
      this.stage.appendChild(box);
      setTimeout(() => box.remove(), 1400);
    }

    // ---- block colours ----
    colorAt(i) {
      if (this.fill.mode === 'solid') return this.fill.color;
      const list = this.fill.list || this.palette;
      return list[i % list.length];
    }

    // keep each block's colour when the name is edited: blocks before and after the edit keep theirs, new ones follow the fill
    syncColors(prev, next) {
      if (this.fill.mode !== 'custom') { this.colors = next.map((_, i) => this.colorAt(i)); return; }
      const old = this.colors;
      let a = 0, b = 0;
      while (a < prev.length && a < next.length && prev[a] === next[a]) a++;
      while (b < prev.length - a && b < next.length - a && prev[prev.length - 1 - b] === next[next.length - 1 - b]) b++;
      this.colors = next.map((_, i) => (i < a ? old[i] : i >= next.length - b ? old[old.length - (next.length - i)] : this.colorAt(i)));
    }

    // a colour chip: paints the picked block if there is one, otherwise every block
    applyColor(hex, btn) {
      if (this.sel != null && this.sel < this.tokens.length) {
        this.colors[this.sel] = hex;
        this.fill = {mode: 'custom'};
        this.sel = null;
        try { localStorage.setItem('squigli_lamp_coach', '1'); } catch (e) { /* private mode */ }   // learned it: stop coaching
        this.pressed('[data-cube-color]', null);
      } else {
        this.fill = {mode: 'solid', color: hex};
        this.colors = this.colors.map(() => hex);
        this.pressed('[data-cube-color]', btn);
      }
      this.pressed('[data-preset]', null);
      this.update();
    }

    // tapping a block (tile or the 3D lamp) picks it, so the next colour paints only that block; tap again to let go
    onTap(k) {
      if (!this.tokens.length || k == null || k >= this.tokens.length) return;   // the example name can't be picked
      this.busy();
      this.sel = this.sel === k ? null : k;
      this.update();
    }

    tokenLabel(tk) { return tk.length > 1 ? (this.symInfo[tk] || {}).label || tk : tk; }
    colorLabel(hex) { return this.colorLabels[(hex || '').toUpperCase()] || this.colorNames[(hex || '').toUpperCase()] || hex; }

    renderTiles() {
      const box = this.querySelector('[data-tiles]');
      if (!box) return;
      const toks = this.tokens;
      box.hidden = !toks.length;
      while (box.children.length > toks.length) box.lastChild.remove();
      while (box.children.length < toks.length) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lamp__tile';
        b.innerHTML = '<span class="lamp__tile-legend" aria-hidden="true"></span>';
        b.addEventListener('click', () => this.onTap([...box.children].indexOf(b)));
        box.appendChild(b);
      }
      toks.forEach((tk, i) => {
        const b = box.children[i], leg = b.firstChild, sym = tk.length > 1 ? this.symInfo[tk] : null, on = this.sel === i;
        b.style.setProperty('--tile', this.colors[i]);
        b.classList.toggle('is-symbol', !!sym);
        b.classList.toggle('is-selected', on);
        leg.textContent = sym ? '' : tk;
        leg.style.setProperty('--legend', sym ? sym.url : 'none');
        b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', `${this.tokenLabel(tk)}, ${this.colorLabel(this.colors[i])}`);
      });
    }

    updateColorHints() {
      const selecting = this.sel != null;
      this.classList.toggle('is-selecting', selecting);
      const hint = this.querySelector('[data-colors-hint]');
      if (hint) {
        hint.textContent = selecting ? this.t('colors_hint_selected', {letter: this.tokenLabel(this.tokens[this.sel]) || this.t('this_block')})
          : this.t(this.tokens.length ? 'colors_hint_more' : 'colors_hint');
        hint.classList.toggle('is-active', selecting);
      }
      const coach = this.querySelector('[data-coach]');
      if (coach) {
        let learned = false;
        try { learned = localStorage.getItem('squigli_lamp_coach') === '1'; } catch (e) { /* private mode */ }
        // keep inviting until they've coloured one block on its own once
        coach.hidden = learned || selecting || this.tokens.length < Math.max(2, this.min);
      }
    }

    flashHint(msg) { const h = this.querySelector('[data-hint]'); h.textContent = msg; h.classList.add('is-warn'); }

    update(nameChanged = false) {
      const shown = this.shownTokens(), colors = this.colors.slice();
      if (this.lamp) {
        if (nameChanged) this.lamp.setName(shown, colors); else this.lamp.setColors(colors);
        if (this.lamp.setSelected) this.lamp.setSelected(this.sel);
        this.showSize();
      }
      if (this.flatEl) this.renderFlat();
      this.renderTiles();
      this.updateColorHints();
      const n = this.tokens.length, hint = this.querySelector('[data-hint]');
      hint.classList.toggle('is-warn', this.latin || n >= this.max || (n > 0 && n < this.min));
      hint.textContent = this.latin ? this.t('hint_latin')
        : n === 0 ? this.t('example', {name: shown.join('')})
        : n < this.min ? this.t('hint_min', {min: this.min})     // lamps start at 3 blocks
        : n >= this.max ? this.t('hint_max', {max: this.max})
        : this.t('hint_count', {n, max: this.max});
      this.syncOrder(colors);
      if (this.shareBtn) {
        this.shareBtn.hidden = !this.tokens.length;
        if (this.shareBtn.hidden && this.closeShare) this.closeShare();
      }
      this.syncUrl();
    }

    showSize() {
      const el = this.querySelector('[data-size-text]');   // next to the ruler icon, like the Christmas preview
      el.textContent = this.t('size', {h: Math.round(this.lamp.heightMm() / 10), w: Math.round(this.lamp.widthMm() / 10)});
    }

    // ---- order: WhatsApp message or the cart form, in English (the admin reads it) ----
    orderText(colors, toks = this.tokens) {
      const named = toks.map((t) => (t.length > 1 ? `[${t[0].toUpperCase()}${t.slice(1)}]` : t)).join('');
      const colorList = toks.map((t, k) => `${t.length > 1 ? t[0].toUpperCase() + t.slice(1) : t}:${this.colorNames[colors[k].toUpperCase()] || colors[k]}`).join(', ');
      const base = `${this.base[0].toUpperCase()}${this.base.slice(1)}, ${this.colorNames[this.baseColor.toUpperCase()] || this.baseColor}`;
      const shade = `${this.shade[0].toUpperCase()}${this.shade.slice(1)}`;
      return {named, colorList, base, shade};
    }

    syncOrder(colors) {
      const o = this.orderText(colors), ok = this.tokens.length >= this.min;
      const notify = this.querySelector('[data-wa-notify]');
      if (notify) {
        const d = this.orderText(colors, this.shownTokens());   // with no name typed yet, the example they're looking at
        const msg = this.t('notify_msg', {name: d.named, base: d.base, shade: d.shade, colors: d.colorList});
        notify.href = `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(msg)}`;
      }
      const wa = this.querySelector('[data-wa-order]');
      if (wa) {
        const msg = this.t('wa_msg', {name: o.named || '—', base: o.base, shade: o.shade, colors: o.colorList || '—'});
        wa.href = `https://wa.me/${this.dataset.whatsapp}?text=${encodeURIComponent(msg)}`;
        wa.classList.toggle('is-disabled', !ok);
        wa.setAttribute('aria-disabled', String(!ok));
      }
      const form = this.querySelector('[data-cart-form]');
      if (form) {
        const n = this.tokens.length;
        const v = this.variants.find((x) => x.available && x.options.some((op) => String(op).match(new RegExp(`(^|\\D)${n}(\\D|$)`))))
          || this.variants.find((x) => x.available);
        form.querySelector('[data-variant-id]').value = v ? v.id : '';
        form.querySelector('[data-prop-name]').value = o.named;
        form.querySelector('[data-prop-colors]').value = o.colorList;
        form.querySelector('[data-prop-base]').value = o.base;
        form.querySelector('[data-prop-shade]').value = o.shade;
        // without a name the button still answers a tap (it sends them to the name box), so it's only dimmed, not disabled
        const btn = form.querySelector('[data-submit]');
        btn.disabled = !v;
        btn.classList.toggle('is-waiting', !ok);
        btn.setAttribute('aria-disabled', String(!ok));
        form.querySelector('[data-buy-note]').hidden = ok;
        const price = this.querySelector('[data-price]');
        if (price) {
          const money = (c) => `${this.dataset.currency}${(c / 100).toFixed(c % 100 ? 2 : 0)}`;
          const from = Math.min(...this.variants.filter((x) => x.available).map((x) => x.price));
          price.textContent = !v ? '' : ok ? money(v.price) : this.t('price_from', {price: money(from)});
          price.classList.toggle('is-from', !!v && !ok);
        }
      }
    }

    askForName() {                      // Add to cart before there's a name: take them to the name box
      const input = this.input;
      this.flashHint(this.tokens.length ? this.t('hint_min', {min: this.min}) : this.t('hint_type'));
      input.scrollIntoView({block: 'center', behavior: this.still ? 'auto' : 'smooth'});
      input.focus({preventScroll: true});
      input.classList.remove('is-nudge');
      void input.offsetWidth;                         // restart the glow if they tap again
      input.classList.add('is-nudge');
      input.addEventListener('animationend', () => input.classList.remove('is-nudge'), {once: true});
    }

    async addToCart(e, form) {
      if (this.tokens.length < this.min) { e.preventDefault(); this.askForName(); return; }
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;              // no drawer: let the form post normally
      e.preventDefault();
      const btn = form.querySelector('[data-submit]');
      btn.disabled = true;
      try {
        const body = new FormData(form);
        body.append('sections', drawer.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
        const res = await fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}`, {method: 'POST', headers: {Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest'}, body}).then((r) => r.json());
        if (res.status) throw new Error(res.description || res.message);
        drawer.renderContents(res);
        drawer.classList.remove('is-empty');
      } catch (err) {
        this.flashHint(this.t('could_not_add'));
      } finally { btn.disabled = false; }
    }

    // ---- share links (see the top): read on load, mirrored into the address bar, shared from the stage ----
    hexFor(name) {                                   // a palette name ('pink') -> its hex, as the colour buttons have it
      const want = String(name || '').toLowerCase();
      return Object.keys(this.colorNames).find((hex) => this.colorNames[hex].toLowerCase() === want) || null;
    }
    linkName(hex) { return (this.colorNames[(hex || '').toUpperCase()] || '').toLowerCase(); }

    readLink() {
      let q;
      try { q = new URLSearchParams(location.search); } catch (e) { return; }
      const raw = q.get('name');
      if (!raw) return;
      const bySlug = {};
      for (const [emoji, s] of Object.entries(this.symbols)) bySlug[s.slug] = emoji;
      this.input.value = raw.replace(/\(([a-z]+)\)/gi, (m, w) => bySlug[w.toLowerCase()] || '');
      this.cycling = false;                          // their design, not the examples
      this.readInput();
      const btn = (sel, key, val) => [...this.querySelectorAll(sel)].find((b) => (b.dataset[key] || '').toUpperCase() === String(val || '').toUpperCase());
      const list = (q.get('colors') || '').split('-').map((c) => btn('[data-cube-color]', 'cubeColor', this.hexFor(c))).filter(Boolean).map((b) => b.dataset.cubeColor);
      if (list.length === 1) this.fill = {mode: 'solid', color: list[0]};
      else if (list.length && list.length >= this.tokens.length) this.fill = {mode: 'custom'};
      else if (list.length) { this.palette = list; this.fill = {mode: 'pattern', list}; }
      if (list.length) {
        this.colors = this.tokens.map((_, i) => (this.fill.mode === 'custom' ? list[i] : this.colorAt(i)));
        this.pressed('[data-preset]', null);
        this.pressed('[data-cube-color]', this.fill.mode === 'solid' ? btn('[data-cube-color]', 'cubeColor', list[0]) : null);
      }
      const [shape, baseName] = (q.get('base') || '').split('-');
      const baseBtn = btn('[data-base]', 'base', shape), colorBtn = btn('[data-base-color]', 'baseColor', this.hexFor(baseName)), shadeBtn = btn('[data-shade]', 'shade', q.get('shade'));
      if (baseBtn) { this.base = baseBtn.dataset.base; this.checked('[data-base]', baseBtn); }
      if (colorBtn) { this.baseColor = colorBtn.dataset.baseColor; this.pressed('[data-base-color]', colorBtn); }
      if (shadeBtn) { this.shade = shadeBtn.dataset.shade; this.checked('[data-shade]', shadeBtn); }
      this.update(true);
    }

    shareUrl() {
      const name = this.tokens.map((t) => (t.length > 1 ? `(${t})` : t)).join('');
      const parts = [`name=${encodeURIComponent(name)}`];
      let colors = '';
      if (this.fill.mode === 'solid') colors = this.linkName(this.fill.color);
      else if (this.fill.mode === 'pattern' && this.fill.list.join() !== this.rainbow.join()) colors = this.fill.list.map((h) => this.linkName(h)).join('-');
      else if (this.fill.mode === 'custom') colors = this.colors.slice(0, this.tokens.length).map((h) => this.linkName(h)).join('-');
      if (colors) parts.push(`colors=${colors}`);
      parts.push(`base=${this.base}-${this.linkName(this.baseColor)}`, `shade=${this.shade}`);
      return `${location.origin}${this.dataset.productUrl || location.pathname}?${parts.join('&')}`;
    }
    shareText() {
      const n = this.tokens.filter((t) => t.length === 1).join('');
      return n ? this.t('share_text_named', {name: n}) : this.t('share_text');
    }

    // keep the design in the address bar, so a reload or a copied address keeps it
    syncUrl() {
      if (!this.ownsUrl || !window.history || !history.replaceState) return;
      clearTimeout(this.urlTimer);
      this.urlTimer = setTimeout(() => {
        const keep = new URLSearchParams(location.search);
        LINK_KEYS.forEach((k) => keep.delete(k));
        const ours = this.tokens.length ? this.shareUrl().split('?')[1] : '';
        const search = [ours, keep.toString()].filter(Boolean).join('&');
        const url = location.pathname + (search ? `?${search}` : '') + location.hash;
        if (url !== location.pathname + location.search + location.hash) {
          try { history.replaceState(history.state, '', url); } catch (e) { /* not allowed here */ }
        }
      }, 300);
    }

    copyText(text) {
      if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
      const ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e) { /* no clipboard */ }
      ta.remove();
      return Promise.resolve();
    }

    initShare() {
      this.shareBtn = this.querySelector('[data-share]');
      const menu = this.querySelector('[data-share-menu]'), wa = this.querySelector('[data-share-wa]'), copy = this.querySelector('[data-share-copy]');
      if (!this.shareBtn || !menu) return;
      const setMenu = (open) => {
        menu.hidden = !open;
        this.shareBtn.setAttribute('aria-expanded', String(open));
        if (open && wa) wa.href = `https://wa.me/?text=${encodeURIComponent(`${this.shareText()}: ${this.shareUrl()}`)}`;
      };
      this.closeShare = () => setMenu(false);
      this.shareBtn.addEventListener('click', () => {
        // phones: the share sheet (WhatsApp, Instagram, Messages...); elsewhere a small menu
        if (navigator.share && window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
          navigator.share({title: 'Squigli', text: this.shareText(), url: this.shareUrl()}).catch(() => {});
          return;
        }
        setMenu(menu.hidden);
      });
      if (wa) wa.addEventListener('click', () => setTimeout(() => setMenu(false), 0));
      if (copy) copy.addEventListener('click', () => this.copyText(this.shareUrl()).then(() => {
        copy.textContent = this.t('link_copied');
        setTimeout(() => { copy.textContent = this.t('copy_link'); setMenu(false); }, 1400);
      }));
      document.addEventListener('click', (e) => { if (!menu.hidden && !e.target.closest('.lamp__share-wrap')) setMenu(false); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) { setMenu(false); this.shareBtn.focus(); } });
    }

    // ---- flat preview: when this device can't run the 3D one ----
    // A drawing of the lamp (shade in its shape, the blocks with their letters, slightly staggered like the real stack,
    // the base in its shape and colour) plus a note that the 3D preview isn't working here. Blocks can still be tapped.
    initFlat() {
      this.querySelector('[data-loading]').hidden = true;
      this.flatEl = document.createElement('div');
      this.flatEl.className = 'lamp__flat';
      this.flatEl.innerHTML = '<div class="lamp__flat-lamp"><span class="lamp__flat-shade" aria-hidden="true"></span>'
        + '<div class="lamp__flat-stack" data-flat-stack></div><span class="lamp__flat-base" aria-hidden="true"></span></div>';
      this.stage.appendChild(this.flatEl);
      const note = document.createElement('p');
      note.className = 'lamp__no3d';
      note.textContent = this.t('no3d');
      this.stage.appendChild(note);
      this.classList.add('is-ready', 'is-flat');
      new ResizeObserver(() => this.renderFlat()).observe(this.stage);
      this.renderFlat();
      this.startShow();
      this.startCycle();
    }

    renderFlat() {
      const toks = this.shownTokens(), stack = this.flatEl.querySelector('[data-flat-stack]'), example = !this.tokens.length;
      while (stack.children.length > toks.length) stack.lastChild.remove();
      while (stack.children.length < toks.length) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lamp__flat-block';
        b.innerHTML = '<span class="lamp__flat-legend" aria-hidden="true"></span>';
        b.addEventListener('click', () => this.onTap([...stack.children].indexOf(b)));
        stack.appendChild(b);
      }
      toks.forEach((tk, k) => {
        const b = stack.children[k], leg = b.firstChild, sym = tk.length > 1 ? this.symInfo[tk] : null;
        b.style.setProperty('--block', this.colors[k]);
        b.style.setProperty('--dx', `${(k % 2 ? -1 : 1) * (3 + ((tk.charCodeAt(0) + k) % 3))}%`);   // hand-assembled stagger
        b.classList.toggle('is-symbol', !!sym);
        b.classList.toggle('is-selected', this.sel === k);
        leg.textContent = sym ? '' : tk;
        leg.style.setProperty('--legend', sym ? sym.url : 'none');
        b.tabIndex = example ? -1 : 0;
        b.setAttribute('aria-label', `${this.tokenLabel(tk)}, ${this.colorLabel(this.colors[k])}`);
      });
      this.flatEl.classList.toggle('is-example', example);
      this.flatEl.dataset.base = this.base;
      this.flatEl.dataset.shade = this.shade;
      this.flatEl.style.setProperty('--base', this.baseColor);
      // block size: the whole lamp (shade ~1.7 blocks, base ~0.35) fits the stage
      const r = this.flatEl.getBoundingClientRect();
      const b = Math.max(22, Math.min(110, r.height / (toks.length + 2.2), r.width / 2.6));
      this.flatEl.style.setProperty('--b', `${Math.floor(b)}px`);
    }

    // ---- guided tour (the "?" on the stage), the same idea as the name studio's ----
    initTour() {
      this.tour = this.querySelector('[data-tour]');
      if (!this.tour) return;
      this.tourPop = this.querySelector('[data-tour-pop]');
      const styleGroup = this.querySelector('[data-base]') && this.querySelector('[data-base]').closest('.lamp__group');
      this.allTourSteps = [
        {sel: '[data-input]', emoji: '✏️', title: this.t('tour_type_title'), body: this.t('tour_type_body')},
        {sel: '.lamp__colors .lamp__chips', emoji: '🎨', title: this.t('tour_color_title'), body: this.t('tour_color_body')},
        {sel: '[data-tiles]', emoji: '🌈', title: this.t('tour_block_title'), body: this.t('tour_block_body'), selectFirst: true, needsName: true},
        {el: styleGroup, emoji: '✨', title: this.t('tour_style_title'), body: this.t('tour_style_body')},
        {sel: '[data-switch]', emoji: '💡', title: this.t('tour_switch_title'), body: this.t('tour_switch_body'), raise: true},
      ];
      this.querySelectorAll('[data-tour-skip]').forEach((el) => el.addEventListener('click', () => this.endTour()));
      this.querySelector('[data-tour-next]').addEventListener('click', () => this.tourNext());
      this.querySelector('[data-tour-prev]').addEventListener('click', () => this.tourPrev());
      const help = this.querySelector('[data-help]');
      if (help) help.addEventListener('click', () => this.startTour());
      window.addEventListener('resize', () => { if (!this.tour.hidden) this.positionTour(); });
    }

    startTour() {
      this.manual = true;                        // the automatic light show stops for good
      clearTimeout(this.showTimer);
      if (this.lit) this.setLit(false);
      this.tourSteps = this.allTourSteps.filter((s) => (s.el || this.querySelector(s.sel)) && (!s.needsName || this.tokens.length));
      this.tourIdx = 0;
      this.tour.hidden = false;
      this.showTourStep();
    }

    endTour() {
      if (!this.tour) return;
      this.tour.hidden = true;
      this.querySelectorAll('.lamp-tour-target').forEach((e) => e.classList.remove('lamp-tour-target'));
      this.stage.classList.remove('lamp-tour-raise');
      this.sel = null;
      this.update();
    }

    tourNext() { if (this.tourIdx >= this.tourSteps.length - 1) this.endTour(); else { this.tourIdx++; this.showTourStep(); } }
    tourPrev() { if (this.tourIdx > 0) { this.tourIdx--; this.showTourStep(); } }

    showTourStep() {
      const step = this.tourSteps[this.tourIdx];
      this.sel = step.selectFirst && this.tokens.length ? 0 : null;   // the "one block" step really picks the first block
      this.update();
      this.querySelectorAll('.lamp-tour-target').forEach((e) => e.classList.remove('lamp-tour-target'));
      const target = step.el || this.querySelector(step.sel);
      if (target) target.classList.add('lamp-tour-target');
      this.stage.classList.toggle('lamp-tour-raise', !!step.raise);   // the switch lives inside the stage: lift the stage over the dim
      this.tourTarget = target;
      this.querySelector('[data-tour-emoji]').textContent = step.emoji;
      this.querySelector('[data-tour-title]').textContent = `${this.tourIdx + 1}. ${step.title}`;
      this.querySelector('[data-tour-body]').textContent = step.body;
      this.querySelector('[data-tour-dots]').innerHTML = this.tourSteps.map((_, i) => `<span class="lamp__tour-dot${i === this.tourIdx ? ' is-on' : ''}"></span>`).join('');
      this.querySelector('[data-tour-prev]').style.visibility = this.tourIdx === 0 ? 'hidden' : 'visible';
      this.querySelector('[data-tour-next]').textContent = this.tourIdx === this.tourSteps.length - 1 ? this.t('lets_go') : this.t('next');
      this.positionTour();
      if (target && target.scrollIntoView) target.scrollIntoView({block: 'center', behavior: this.still ? 'auto' : 'smooth'});
      setTimeout(() => this.positionTour(), 400);
    }

    positionTour() {
      const target = this.tourTarget, pop = this.tourPop;
      if (!target || !pop) return;
      const r = target.getBoundingClientRect(), pw = pop.offsetWidth, ph = pop.offsetHeight;
      const vw = window.innerWidth, vh = window.innerHeight, gap = 14;
      const left = Math.max(gap, Math.min(r.left + r.width / 2 - pw / 2, vw - pw - gap));
      let top;
      if (r.bottom + gap + ph <= vh) top = r.bottom + gap;             // below the target
      else if (r.top - gap - ph >= 0) top = r.top - gap - ph;          // above it
      else top = Math.max(gap, (vh - ph) / 2);                         // or centred
      pop.style.left = `${left}px`;
      pop.style.top = `${top}px`;
    }

    // ---- 3D ----
    boot3D() {
      const go = () => import(this.dataset.engine)
        .then((m) => m.mount(this.stage, {three: this.dataset.three, cubes: this.dataset.cubes, bases: this.dataset.bases, shades: this.dataset.shades,
          onPick: (k) => this.onTap(k), onMiss: () => { if (this.sel != null) { this.sel = null; this.update(); } }}))
        .then((lamp) => {
          this.lamp = lamp;
          lamp.setBase(this.base, this.baseColor);
          lamp.setShade(this.shade);
          lamp.setName(this.shownTokens(), this.colors.slice());
          this.startCycle();
          if (lamp.setSelected) lamp.setSelected(this.sel);
          this.showSize();
          lamp.onFrame(() => this.placeWall());
          this.querySelector('[data-loading]').hidden = true;
          this.classList.add('is-ready');
          this.startShow();
        })
        .catch(() => this.initFlat());
      const later = () => ('requestIdleCallback' in window ? requestIdleCallback(go, {timeout: 1200}) : setTimeout(go, 150));
      if (document.readyState === 'complete') later(); else window.addEventListener('load', later, {once: true});
    }

    placeWall() {
      if (!this.lit && !this.classList.contains('is-glowing')) return;
      const p = this.lamp.shadeOnScreen();
      this.stage.style.setProperty('--lx', `${(p.x * 100).toFixed(2)}%`);
      this.stage.style.setProperty('--ly', `${(p.y * 100).toFixed(2)}%`);
      this.stage.style.setProperty('--ltop', `${(p.top * 100).toFixed(2)}%`);
    }

    setLit(on) {
      this.lit = on;
      if (this.lamp) { this.lamp.setLight(on); this.placeWall(); }
      document.documentElement.classList.toggle('squigli-lights-out', on);
      if (!on) {
        document.documentElement.classList.remove('squigli-lights-auto');
        if (this.endShow) { document.removeEventListener('pointerdown', this.endShow, true); this.endShow = null; }
      }
      this.classList.toggle('is-glowing', on);
      this.switchBtn.setAttribute('aria-pressed', String(on));
      this.querySelector('[data-switch-label]').textContent = on ? this.t('turn_off') : this.t('turn_on');
    }

    // ---- the switch's sound: a wall light switch (made on the spot with Web Audio, no file to load) ----
    // a sharp plastic snap, the rocker knocking against its stop, then a faint second tick as the spring settles;
    // flicking it on sounds a touch brighter than off. Only for real presses (browsers need the tap to allow sound).
    clack(on) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = this.audio || (this.audio = new AC());
        if (ctx.state === 'suspended') ctx.resume();
        const out = ctx.createGain();
        out.gain.value = 0.55;
        out.connect(ctx.destination);
        const t = ctx.currentTime + 0.01;
        const snap = (at, dur, freq, q, level) => {
          const n = Math.max(1, Math.floor(ctx.sampleRate * dur)), buf = ctx.createBuffer(1, n, ctx.sampleRate), d = buf.getChannelData(0);
          for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, 5);
          const src = ctx.createBufferSource(), bp = ctx.createBiquadFilter(), g = ctx.createGain();
          src.buffer = buf; bp.type = 'bandpass'; bp.frequency.value = freq; bp.Q.value = q; g.gain.value = level;
          src.connect(bp).connect(g).connect(out);
          src.start(at);
        };
        snap(t, 0.018, on ? 3400 : 2900, 1.1, 1.0);                 // the snap
        const knock = ctx.createOscillator(), kg = ctx.createGain();  // the rocker hitting its stop
        knock.type = 'triangle';
        knock.frequency.setValueAtTime(on ? 210 : 175, t);
        knock.frequency.exponentialRampToValueAtTime(80, t + 0.05);
        kg.gain.setValueAtTime(0.0001, t);
        kg.gain.exponentialRampToValueAtTime(0.6, t + 0.002);
        kg.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
        knock.connect(kg).connect(out);
        knock.start(t); knock.stop(t + 0.08);
        snap(t + 0.014, 0.008, on ? 5200 : 4600, 2, 0.28);           // the spring settling
      } catch (e) { /* no sound is fine */ }
    }

    // ---- the automatic on/off show: only while the lamp is on screen and nobody is using the controls ----
    watchView() {
      this.inView = false;
      new IntersectionObserver((es) => {
        this.inView = es[0].intersectionRatio >= 0.55;
        if (!this.inView && this.lit) this.setLit(false);    // never leave the visitor scrolling a dark page
      }, {threshold: [0, 0.55, 1]}).observe(this.stage);
    }

    // When the page opens the lamp switches itself on once, then off once, and that's it. It waits until the lamp is
    // on screen and nobody is busy with the controls; touching the switch or the controls first cancels it.
    startShow() {
      if (this.still) return;
      const HOLD = 4200;
      let phase = 'wait';
      const step = () => {
        if (this.manual) return;
        if (phase === 'wait') {
          if (this.inView && performance.now() > this.busyUntil && document.visibilityState === 'visible') {
            this.setLit(true); phase = 'on';
            // the show's dark room lets clicks through, so the page keeps working; any press ends the show
            document.documentElement.classList.add('squigli-lights-auto');
            this.endShow = (e) => { if (!this.switchBtn.contains(e.target) && this.lit && !this.manual) this.setLit(false); };
            document.addEventListener('pointerdown', this.endShow, true);
            this.showTimer = setTimeout(step, HOLD);
          } else this.showTimer = setTimeout(step, 800);
        } else if (phase === 'on') {
          if (this.lit) this.setLit(false);
          phase = 'done';
        }
      };
      this.showTimer = setTimeout(step, 1600);
    }

    disconnectedCallback() {
      clearTimeout(this.showTimer);
      document.documentElement.classList.remove('squigli-lights-out', 'squigli-lights-auto');
      if (this.endShow) document.removeEventListener('pointerdown', this.endShow, true);
    }
  }

  customElements.define('lamp-builder', LampBuilder);
})();
